import React, { useEffect, useState } from "react";

import { API_BASE_URL } from "./config";
import {BRAND, contrastText, getThemeColors, inputStyle} from "./themes";

export default function ResetPassword({theme}) {
  // states: init|ready|submitting|ok|error
  const [status, setStatus] = useState("init");
  const [fieldError, setFieldError] = useState({ field: null, msg: "" });

  const [email, setEmail] = useState("");
  const [token, setToken] = useState("");

  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");

  const themeColors = getThemeColors(theme);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const e = params.get("email");
    const t = params.get("token");

    if (!e || !t) {
      setStatus("error");
      setFieldError({ field: "general", msg: "Missing email or token." });
      return;
    }

    setEmail(e);
    setToken(t);
    setStatus("ready");
  }, []);

  async function submit(e) {
    e.preventDefault();
    setFieldError({ field: null, msg: "" });

    if (!newPassword.trim() || newPassword.length < 8) {
      setFieldError({ field: "password", msg: "Invalid password." });
      return;
    }
    if (newPassword !== confirm) {
      setFieldError({ field: "confirm", msg: "Passwords do not match." });
      return;
    }

    setStatus("submitting");

    try {
      const res = await fetch(`${API_BASE_URL}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          token,
          new_password: newPassword,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Reset failed.");
      }

      setStatus("ok");
    } catch (err) {
      setFieldError({ field: "password", msg: err.message || "Reset failed." });
      setStatus("ready");
    }
  }

  return (
      <div style={{ 
        padding: 28, 
        background: themeColors.background, 
        minHeight: '100vh',
        fontFamily: BRAND.font
      }}>
        <img
          src="/logo_wordmark.png"
          alt="Loopen"
          style={{
            height: 48,
            width: 'auto',
            objectFit: 'contain',
            filter: theme === 'dark' ? 'invert(1) brightness(1.2)' : 'none',
            marginBottom: 8,
          }}
        />
        <div style={{ fontSize: 11, color: themeColors.textMuted, marginBottom: 24 }}>by Lausanne Business Solutions</div>

        <div style={{ maxWidth: 480 }}>
          <div style={{
            background: themeColors.cardBg,
            borderRadius: 10,
            padding: 32,
            border: `1px solid ${themeColors.borderColor}`,
          }}>
            {status === "init" && <div style={{ color: themeColors.text }}>Loading…</div>}

            {status === "ready" && (
              <>
                <h1 style={{ margin: '0 0 6px', fontSize: 28, color: themeColors.text, fontWeight: 700 }}>
                  Reset Password
                </h1>
                <p style={{ margin: '0 0 16px', fontSize: 14, color: themeColors.textMuted }}>
                  Enter the new password for <b style={{ color: themeColors.text }}>{email}</b> below.
                </p>
                <form onSubmit={submit}>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 4, color: themeColors.text, fontWeight: 600, fontSize: 13 }}>
                      Password
                    </label>
                    <input
                      type="password"
                      value={newPassword}
                      onChange={(e) => { setNewPassword(e.target.value); setFieldError({ field: null, msg: "" }); }}
                      placeholder="••••••••"
                      style={{
                        ...inputStyle(themeColors),
                        border: fieldError.field === "password" ? `1px solid ${BRAND.error}` : `1px solid ${themeColors.borderColor}`,
                      }}
                      required
                    />
                    {fieldError.field === "password" && (
                      <div style={{ color: BRAND.error, fontSize: 13, marginTop: 4, fontWeight: 500 }}>
                        {fieldError.msg}
                      </div>
                    )}
                  </div>

                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', marginBottom: 4, color: themeColors.text, fontWeight: 600, fontSize: 13 }}>
                      Confirm password
                    </label>
                    <input
                      type="password"
                      value={confirm}
                      onChange={(e) => { setConfirm(e.target.value); setFieldError({ field: null, msg: "" }); }}
                      placeholder="••••••••"
                      style={{
                        ...inputStyle(themeColors),
                        border: fieldError.field === "confirm" ? `1px solid ${BRAND.error}` : `1px solid ${themeColors.borderColor}`,
                      }}
                      required
                    />
                    {fieldError.field === "confirm" && (
                      <div style={{ color: BRAND.error, fontSize: 13, marginTop: 4, fontWeight: 500 }}>
                        {fieldError.msg}
                      </div>
                    )}
                  </div>

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    style={{
                      padding: '14px 28px',
                      borderRadius: 8,
                      background: BRAND.accent,
                      color: contrastText(BRAND.accent),
                      border: 'none',
                      cursor: status === "submitting" ? "not-allowed" : "pointer",
                      fontWeight: 700,
                      width: '100%',
                      fontSize: 16,
                      opacity: status === "submitting" ? 0.7 : 1,
                      marginTop: 12,
                    }}
                  >
                    {status === "submitting" ? "Updating…" : "Update password"}
                  </button>
                </form>
              </>
            )}

            {status === "ok" && (
              <>
                <div style={{ fontSize: 18, fontWeight: 700, color: themeColors.text, marginBottom: 12 }}>
                  ✅ Password successfully reset
                </div>
                <a href="/login"
                  style={{
                    display: 'inline-block',
                    padding: '10px 14px',
                    borderRadius: 8,
                    background: themeColors.primary,
                    color: '#fff',
                    fontWeight: 600,
                    textDecoration: 'none',
                    fontSize: 14,
                  }}
                >
                  Go to Sign in
                </a>
              </>
            )}

            {status === "error" && (
              <div style={{ color: BRAND.error, fontSize: 14, fontWeight: 500 }}>
                {fieldError.msg}
                <p style={{ marginTop: 12 }}>
                  <a href="/login" style={{ color: themeColors.primary }}>Go to login</a>
                </p>
              </div>
            )}
            
          </div>
          <div style={{ fontSize: 12, color: themeColors.textMuted, marginTop: 16 }}>
            © {new Date().getFullYear()} Loopen
          </div>
        </div>
      </div>
    );
}